import { useMemo } from 'react';
import { useMyItems } from '@/hooks/useUserItems';

/**
 * Normalizes a title by lowercasing and taking the first 3 words.
 */
function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .join(' ');
}

/**
 * Normalizes a card number by removing leading zeros and common prefixes.
 */
function normalizeCardNumber(cardNumber: string): string {
  return cardNumber
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/^0+/, '')
    .trim();
}

/**
 * Generate a loose match key for duplicate detection.
 * Uses normalized title (first 3 words), category, and card number.
 */
function generateLooseMatchKey(item: {
  title: string;
  category: string;
  card_number?: string | null;
}): string {
  return [
    normalizeTitle(item.title),
    item.category,
    normalizeCardNumber(item.card_number || ''),
  ].join('|');
}

interface UseDuplicateDetectionParams {
  title: string;
  category: string;
  cardNumber?: string;
  excludeItemId?: string; // For edit mode - exclude the current item
}

interface DuplicateItem {
  id: string;
  title: string;
  card_number?: string | null;
}

interface UseDuplicateDetectionResult {
  potentialDuplicates: DuplicateItem[];
  hasDuplicates: boolean;
}

/**
 * Hook to detect potential duplicate items in the user's inventory.
 * Returns a list of items that match based on normalized title, category, and card number.
 */
export function useDuplicateDetection({
  title,
  category,
  cardNumber,
  excludeItemId,
}: UseDuplicateDetectionParams): UseDuplicateDetectionResult {
  const { data: myItems = [] } = useMyItems();

  const potentialDuplicates = useMemo(() => {
    // Don't check if title is too short
    if (!title || title.length < 8 || !category) {
      return [];
    }

    const inputKey = generateLooseMatchKey({
      title,
      category,
      card_number: cardNumber,
    });

    return myItems.filter((item) => {
      // Skip the current item in edit mode
      if (excludeItemId && item.id === excludeItemId) {
        return false;
      }

      // Skip if different category
      if (item.category !== category) {
        return false;
      }

      const itemKey = generateLooseMatchKey({
        title: item.title,
        category: item.category,
        card_number: item.card_number,
      });

      return inputKey === itemKey;
    }).map((item) => ({
      id: item.id,
      title: item.title,
      card_number: item.card_number,
    }));
  }, [title, category, cardNumber, excludeItemId, myItems]);

  return {
    potentialDuplicates,
    hasDuplicates: potentialDuplicates.length > 0,
  };
}
